import { useCallback, useEffect, useRef, useState } from 'react';
import axiosCall from '../../services/axios';
import { useAuthContext } from '../../contexts/AuthContext';
import { LossDraftRepair } from '../../types/LossDraftRepairTypes';

type LossDraftRepairValue = LossDraftRepair | LossDraftRepair[] | null | undefined;

interface WrappedResponse {
  result?: LossDraftRepair[];
  data?: LossDraftRepair[];
}

type LossDraftRepairResponse = LossDraftRepair | LossDraftRepair[] | WrappedResponse;

const getSingleLossDraftRepair = (response: LossDraftRepairResponse): LossDraftRepair | null => {
  if (Array.isArray(response)) {
    return response[0] ?? null;
  }

  if ('result' in response && response.result !== undefined) {
    if (Array.isArray(response.result)) {
      return response.result[0] ?? null;
    }

    return response.result ?? null;
  }

  if ('data' in response && response.data !== undefined) {
    if (Array.isArray(response.data)) {
      return response.data[0] ?? null;
    }

    return response.data ?? null;
  }

  return response as LossDraftRepair;
};

const useGetLossDraftRepair = (loanNumber: string, claimId: string | null) => {
  const { token } = useAuthContext();

  const [lossDraftRepair, setLossDraftRepair] = useState<LossDraftRepair | null>(null);
  const [loadingLossDraftRepair, setLoadingLossDraftRepair] = useState<boolean>(false);
  const [lossDraftRepairError, setLossDraftRepairError] = useState<string>('');
  const [hasLoadedLossDraftRepair, setHasLoadedLossDraftRepair] = useState(false);

  const requestIdRef = useRef(0);

  const getLossDraftRepair = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!loanNumber || claimId === null || !token) {
      setLossDraftRepair(null);
      return;
    }

    const endpoint = process.env.REACT_APP_LOSS_DRAFT_REPAIR_LOAN_CLAIMID;

    if (!endpoint) {
      setLossDraftRepairError('REACT_APP_LOSS_DRAFT_REPAIR_LOAN_CLAIMID is not configured.');
      return;
    }

    setLoadingLossDraftRepair(true);
    setHasLoadedLossDraftRepair(false);
    setLossDraftRepairError('');

    try {
      const url = endpoint.replace('?', String(loanNumber)).replace('?', String(claimId));

      const request = {
        url,
        method: 'get',
      };

      const config = {};

      const headers = {
        'Session-Token': token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

      const response = (await axiosCall(request, config, headers)) as LossDraftRepairResponse;

      if (requestId !== requestIdRef.current) {
        return;
      }

      const record = getSingleLossDraftRepair(response);
      setLossDraftRepair(record);
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      console.error('Error retrieving Loss Draft Repair:', error);

      setLossDraftRepair(null);
      setLossDraftRepairError('Unable to retrieve Loss Draft Repair details.');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingLossDraftRepair(false);
        setHasLoadedLossDraftRepair(true);
      }
    }
  }, [loanNumber, claimId, token]);

  const updateLossDraftRepair = useCallback(
    async (updates: Partial<LossDraftRepair>): Promise<boolean> => {
      if (!loanNumber || claimId === null || !token) {
        return false;
      }

      const endpoint = process.env.REACT_APP_LOSS_DRAFT_REPAIR_LOAN_CLAIMID;

      if (!endpoint) {
        setLossDraftRepairError('REACT_APP_LOSS_DRAFT_REPAIR_LOAN_CLAIMID is not configured.');
        return false;
      }

      const url = endpoint.replace('?', String(loanNumber)).replace('?', String(claimId));

      const request = {
        url,
        method: 'post',
        data: updates,
      };

      const config = {};

      const headers = {
        'Session-Token': token,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      };

      try {
        await axiosCall(request, config, headers);
        await getLossDraftRepair();

        return true;
      } catch (error) {
        console.error('Error updating Loss Draft Repair:', error);
        setLossDraftRepairError('Unable to update Loss Draft Repair details.');
        return false;
      }
    },
    [loanNumber, claimId, token, getLossDraftRepair],
  );

  useEffect(() => {
    getLossDraftRepair();

    return () => {
      requestIdRef.current += 1;
    };
  }, [getLossDraftRepair]);

  return {
    lossDraftRepair,
    loadingLossDraftRepair,
    lossDraftRepairError,
    hasLoadedLossDraftRepair,
    getLossDraftRepair,
    updateLossDraftRepair,
  };
};

export default useGetLossDraftRepair;
